import {
  ExternalAuthContext,
  ExternalAuthError,
  ExternalAuthProvider,
  ExternalAuthFlow,
} from '@account/account-portal/external-auth'
import {
  setExternalAuthError,
  onceBeforeAuthenticationSuccess,
  getAuthentication,
  reloadSession,
  setExternalAuthData,
} from '@account/account-portal/lib'
import { KmcUser } from '@account/account-portal/types'

import { KmcAuthResponse } from '../types'

import { AuthHandlerFactory } from './handler-types'

export const createKmcHandler: AuthHandlerFactory = (flow, { apiClient }) => ({
  async handleAuth({ code }) {
    validateProvider(flow.provider)
    const { userData, userDataToken } = await apiClient.kmcAuth(code)

    // KMC flow for 2 different purposes.
    // 1) To update email or phone number
    // 2) For a new registration
    const isProfileUpdate = isKmcProfileUpdateFlow(flow)
    if (isProfileUpdate) {
      await reloadSession({
        logoutIfUnauthenticated: false,
      }).catch(() => undefined)
    }

    // registration flow?
    // 1) set the kmc data
    const { authContext, isAuthenticated, isPersistent, user } = getAuthentication()
    const authData = {
      user,
      isAuthenticated,
      isPersistent,
    }
    setExternalAuthData<ExternalAuthContext>({
      kmc: {
        data: transformKmcData(userData),
        accessToken: userDataToken,
      },
      authContext: {
        ...authContext,
        hasUser: true,
        authData: isProfileUpdate ? authData : undefined,
      },
    })

    if (!isProfileUpdate) {
      const previousAuthContext = {
        ...authContext,
      }
      // 2) broadcast another external auth success once completed
      onceBeforeAuthenticationSuccess(user => {
        const { authContext } = getAuthentication()
        setExternalAuthData<ExternalAuthContext>({
          authContext: {
            ...previousAuthContext,
            ...authContext,
            isAuthHandled: true,
            authData: {
              user,
              isAuthenticated: true,
              isPersistent: user.isPersistent,
            },
          },
        })
      })
    }
  },

  async handleError(err: ExternalAuthError) {
    setExternalAuthError(err.serialize())
  },
})

export function isKmcProfileUpdateFlow(flow: ExternalAuthFlow): boolean {
  return flow.method === 'kmc' && !!flow.transitData?.updateProfileLater
}

export function isKmcRegistrationFlow(flow: ExternalAuthFlow): boolean {
  return flow.method === 'kmc' && !isKmcProfileUpdateFlow(flow)
}

function validateProvider(provider: ExternalAuthProvider) {
  if (provider !== 'kmc') {
    throw new ExternalAuthError(`Invalid provider: ${provider}`)
  }
}

const transformKmcData = ({ firstName, phoneNumber, dateOfBirth, gender }: KmcAuthResponse['userData']): KmcUser => {
  const map: Record<string, KmcUser['gender'] | undefined> = {
    male: 'Male',
    female: 'Female',
  }

  return {
    firstName,
    phoneNumber,
    dobFields: dateOfBirth,
    gender: map[gender || ''],
  }
}
